"use client";

import Link from "next/link";
import React from "react";
import { Loader2 } from "lucide-react";

type Variant =
  | "primary" // Solid mygreen, used e.g. 404 primary CTA
  | "secondary" // White bg, mygreen text, used e.g. Header "Cadastrar"
  | "tinted" // Subtle tinted bg (bg-mygreen/30), used e.g. Hero card CTA
  | "outline" // Transparent with mygreen border
  | "text" // Text-only link style (transparent)
  | "gradient" // Brand gradient background
  | "danger"; // Red variant for destructive actions

type Size = "sm" | "md" | "lg";

export type ButtonProps = {
  href?: string;
  external?: boolean; // open in new tab when true
  variant?: Variant;
  size?: Size;
  fullWidth?: boolean;
  rounded?: "md" | "full";
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
  className?: string;
  children: React.ReactNode;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className"> &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "className">;

function cn(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

const baseStyles =
  "inline-flex items-center justify-center whitespace-nowrap font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed select-none";

const sizeStyles: Record<Size, string> = {
  sm: "text-sm px-3 py-2",
  md: "text-sm px-5 py-2.5",
  lg: "text-base px-6 py-3",
};

const roundedStyles: Record<NonNullable<ButtonProps["rounded"]>, string> = {
  md: "rounded-md",
  full: "rounded-full",
};

const variantStyles: Record<Variant, string> = {
  primary:
    "bg-mygreen text-white hover:bg-mygreen/90 focus-visible:ring-mygreen",
  secondary:
    "bg-white text-mygreen hover:text-mygreen/90 focus-visible:ring-mygreen",
  tinted:
    "bg-mygreen/30 text-green-950 hover:bg-mygreen/40 focus-visible:ring-mygreen/50",
  outline:
    "border border-mygreen text-mygreen hover:bg-mygreen/10 focus-visible:ring-mygreen",
  text: "bg-transparent text-mygreen hover:text-mygreen/80 focus-visible:ring-mygreen/40",
  gradient: "text-white hover:opacity-95 focus-visible:ring-mygreen",
  danger: "bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500",
};

const gradientStyle = {
  background:
    "linear-gradient(216.82deg, #049548 28.05%, #F6C304 201.99%), #049548",
} as const;

function Spinner({ className }: { className?: string }) {
  return (
    <Loader2 className={cn("animate-spin", className)} size={16} aria-hidden />
  );
}

export default function Button({
  href,
  external,
  variant = "primary",
  size = "md",
  fullWidth,
  rounded = "full",
  leftIcon,
  rightIcon,
  loading,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const classes = cn(
    baseStyles,
    sizeStyles[size],
    roundedStyles[rounded],
    variantStyles[variant],
    fullWidth && "w-full",
    loading && "relative",
    className
  );

  const content = (
    <>
      {leftIcon && (
        <span className={cn("mr-2", loading && "opacity-0")}>{leftIcon}</span>
      )}
      <span className={loading ? "opacity-0" : undefined}>{children}</span>
      {rightIcon && (
        <span className={cn("ml-2", loading && "opacity-0")}>{rightIcon}</span>
      )}
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner className="text-current" />
        </span>
      )}
    </>
  );

  const style =
    variant === "gradient" ? (gradientStyle as React.CSSProperties) : undefined;

  // For disabled links, render as span instead of Link to prevent navigation
  if (href && (disabled || loading)) {
    return (
      <span
        className={cn(classes, "opacity-60 cursor-not-allowed")}
        style={style}
        aria-disabled="true"
      >
        {content}
      </span>
    );
  }

  if (href) {
    if (external) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={classes}
          style={style}
          aria-disabled={disabled || loading}
          {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </a>
      );
    }
    const anchorProps = rest as Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      "className" | "href"
    >;
    return (
      <Link
        href={href}
        className={classes}
        style={style}
        aria-disabled={disabled || loading}
        {...anchorProps}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={
        (rest as React.ButtonHTMLAttributes<HTMLButtonElement>).type || "button"
      }
      className={classes}
      style={style}
      disabled={disabled || loading}
      {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
    >
      {content}
    </button>
  );
}
